import React from "react";
import { currencySymbol } from "shared/CurrencySymbol";
import { BookingStatus } from "../bookingStatus";

interface Props {
  booking: any;
  status: BookingStatus;
  isHost: boolean;
}

const Row = ({
  label,
  hint,
  value,
  tone,
}: {
  label: React.ReactNode;
  hint?: string;
  value: string;
  tone?: "discount" | "muted";
}) => (
  <div className="flex items-start justify-between gap-4 py-2.5 text-sm">
    <div>
      <div className="text-neutral-700 dark:text-neutral-300">{label}</div>
      {hint && <div className="mt-0.5 max-w-sm text-xs text-neutral-500 dark:text-neutral-400">{hint}</div>}
    </div>
    <div
      className={`shrink-0 font-medium ${
        tone === "discount"
          ? "text-green-700 dark:text-green-400"
          : tone === "muted"
          ? "text-neutral-500 dark:text-neutral-400"
          : "text-neutral-900 dark:text-neutral-100"
      }`}
    >
      {value}
    </div>
  </div>
);

/** Same numbers as the old PropertySummaryCard, role-aware, with hints shown inline (no hover tooltips). */
const PriceBreakdown: React.FC<Props> = ({ booking: b, isHost }) => {
  const money = (n: number) => `${currencySymbol()}${(Number.isFinite(n) ? n : 0).toFixed(2)}`;
  const rent = Number(b?.item_total) || 0;
  const fee = Number(b?.service_tax) || 0;
  const extraGuests = Number(b?.extra_guest_amount) || 0;
  const tax = Number(b?.tax_total) || 0;
  const total = Number(b?.total_amount) || 0;
  const expenses: any[] = b?.invoice?.expenses || [];
  const discounts: any[] = b?.invoice?.discounts || [];
  const payout = total - fee;

  const lines = (
    <>
      {rent > 0 && (
        <Row
          label="First month's rent"
          hint={isHost ? undefined : "Transferred to the host 24 hours after your check-in."}
          value={money(rent)}
        />
      )}
      {fee > 0 && (
        <Row
          label={isHost ? "Service fee (Nomadroof)" : "One-time service fee"}
          hint={isHost ? undefined : "Covers support, secure payment, and your accommodation guarantee."}
          value={money(fee)}
          tone={isHost ? "muted" : undefined}
        />
      )}
      {extraGuests > 0 && <Row label="Extra guests" value={money(extraGuests)} />}
      {tax > 0 && <Row label="Tax" value={money(tax)} />}
      {expenses.map((e, i) => (
        <Row key={`e${i}`} label={<span className="capitalize">{e.category}</span>} value={money(Number(e.amount))} />
      ))}
      {discounts.map((d, i) => (
        <Row key={`d${i}`} label={<span className="capitalize">{d.description}</span>} value={`− ${money(Number(d.amount))}`} tone="discount" />
      ))}
    </>
  );

  return (
    <details className="group rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
      <summary className="flex min-h-[56px] rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C2410C]/40 cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 sm:px-7 [&::-webkit-details-marker]:hidden">
        <span className="text-lg font-semibold text-neutral-900 dark:text-white">{isHost ? "Booking summary" : "Price breakdown"}</span>
        <span className="flex items-center gap-3">
          {total > 0 && <span className="text-base font-semibold text-neutral-900 dark:text-white">{money(total)}</span>}
          <svg className="h-4 w-4 text-neutral-500 transition-transform group-open:rotate-180" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      </summary>

      <div className="border-t border-neutral-100 dark:border-neutral-800 px-5 pb-5 pt-2 sm:px-7 sm:pb-7">
        <div className="divide-y divide-neutral-100 dark:divide-neutral-800">{lines}</div>

        {total > 0 && (
          <div className="mt-2 flex items-baseline justify-between border-t-2 border-neutral-900 dark:border-neutral-100 pt-4">
            <span className="text-base font-semibold text-neutral-900 dark:text-white">{isHost ? "Guest total" : "Total"}</span>
            <span className="text-2xl font-semibold text-neutral-900 dark:text-white">{money(total)}</span>
          </div>
        )}

        {isHost && total > 0 && (
          <div className="mt-4 rounded-xl bg-green-50 dark:bg-green-900/20 px-4 py-3 text-sm text-green-800 dark:text-green-300">
            {fee > 0 && (
              <div className="flex justify-between">
                <span>Nomadroof service fee</span>
                <span>− {money(fee)}</span>
              </div>
            )}
            <div className="flex justify-between font-semibold">
              <span>You receive</span>
              <span>{money(payout)}</span>
            </div>
          </div>
        )}
      </div>
    </details>
  );
};

export default PriceBreakdown;
